import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
    Percent, Save, DollarSign, Store, History,
    ToggleLeft, ToggleRight, ChevronDown, ChevronUp,
    Building2, Clock, TrendingUp, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';

const TABS = [
    { id: 'config', label: 'Configuración', icon: Percent },
    { id: 'history', label: 'Historial', icon: History },
    { id: 'settlements', label: 'Liquidaciones', icon: Building2 },
];

function fmt(n) {
    return Number(n || 0).toLocaleString('es-MX', { style: 'currency', currency: 'MXN' });
}

function ConfigTab({ config, setConfig, superCats, saving, onSave }) {
    const setSuperCat = (slug, field, value) => {
        setConfig(prev => ({
            ...prev,
            super_categories: prev.super_categories.map(sc =>
                sc.slug === slug ? { ...sc, [field]: value } : sc
            ),
        }));
    };

    const toggleSuperCat = (slug) => {
        setConfig(prev => ({
            ...prev,
            super_categories: prev.super_categories.map(sc =>
                sc.slug === slug ? { ...sc, active: !sc.active } : sc
            ),
        }));
    };

    if (!config) return <div className="text-sm text-muted-foreground py-8 text-center">Cargando configuración…</div>;

    return (
        <div className="space-y-6">
            {/* Default commission */}
            <div className="rounded-xl border bg-card p-5 space-y-4">
                <div className="flex items-center gap-2">
                    <Percent className="h-4 w-4 text-red-600" />
                    <h2 className="font-medium">Comisión global por defecto</h2>
                </div>
                <p className="text-xs text-muted-foreground">
                    Se aplica a OkNegocios cuya super categoría no tenga configuración específica o esté inactiva.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                        <Label className="text-xs">Comisión base (%)</Label>
                        <div className="flex items-center gap-1.5 mt-1">
                            <Input type="number" min="0" max="100" step="0.5"
                                value={config.default_commission_pct}
                                onChange={e => setConfig(p => ({ ...p, default_commission_pct: parseFloat(e.target.value) || 0 }))}
                                className="h-9" />
                            <span className="text-sm text-muted-foreground">%</span>
                        </div>
                    </div>
                    <div>
                        <Label className="text-xs">Cargo fijo por defecto (MXN)</Label>
                        <div className="flex items-center gap-1.5 mt-1">
                            <span className="text-sm text-muted-foreground">$</span>
                            <Input type="number" min="0" step="0.5"
                                value={config.default_fixed_fee}
                                onChange={e => setConfig(p => ({ ...p, default_fixed_fee: parseFloat(e.target.value) || 0 }))}
                                className="h-9" />
                        </div>
                    </div>
                    <div>
                        <Label className="text-xs">Pedido mínimo por defecto (MXN)</Label>
                        <div className="flex items-center gap-1.5 mt-1">
                            <span className="text-sm text-muted-foreground">$</span>
                            <Input type="number" min="0" step="1"
                                value={config.default_min_order}
                                onChange={e => setConfig(p => ({ ...p, default_min_order: parseFloat(e.target.value) || 0 }))}
                                className="h-9" />
                        </div>
                    </div>
                </div>
                <div>
                    <Label className="text-xs">Tipo de comisión por defecto</Label>
                    <div className="flex gap-2 mt-1">
                        {[
                            { value: 'percentage', label: 'Porcentaje' },
                            { value: 'fixed', label: 'Fijo' },
                            { value: 'both', label: '% + Fijo' },
                        ].map(opt => (
                            <button key={opt.value} type="button"
                                onClick={() => setConfig(p => ({ ...p, default_commission_type: opt.value }))}
                                className={`px-3 py-1.5 rounded-md text-xs border transition-colors ${config.default_commission_type === opt.value ? 'bg-red-600 text-white border-red-600' : 'border-input text-muted-foreground hover:bg-secondary'}`}>
                                {opt.label}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* Per super-category config */}
            <div className="rounded-xl border bg-card overflow-hidden">
                <div className="px-5 py-4 border-b">
                    <div className="flex items-center gap-2">
                        <Store className="h-4 w-4 text-red-600" />
                        <h2 className="font-medium">Comisiones por Super Categoría</h2>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                        Configura comisiones específicas por tipo de negocio. Las activas sobreescriben la comisión global.
                    </p>
                </div>
                <div className="divide-y">
                    {(config.super_categories || []).length === 0 ? (
                        <div className="p-6 text-center text-sm text-muted-foreground">
                            No hay super categorías configuradas. Crea super categorías primero.
                        </div>
                    ) : (config.super_categories || []).map(sc => (
                        <div key={sc.slug} className="p-4 space-y-3">
                            <div className="flex items-center gap-3">
                                <div className="text-xl w-8 text-center">{sc.icon || '🏷️'}</div>
                                <div className="flex-1">
                                    <div className="flex items-center gap-2">
                                        <span className="text-sm font-medium">{sc.name}</span>
                                        <span className="text-xs text-muted-foreground font-mono">{sc.slug}</span>
                                    </div>
                                </div>
                                <button type="button" onClick={() => toggleSuperCat(sc.slug)}
                                    className={`flex items-center gap-1.5 text-xs ${sc.active ? 'text-green-600' : 'text-muted-foreground'}`}>
                                    {sc.active ? <ToggleRight className="h-5 w-5" /> : <ToggleLeft className="h-5 w-5" />}
                                    {sc.active ? 'Activa' : 'Inactiva'}
                                </button>
                            </div>
                            {sc.active && (
                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pl-11">
                                    <div>
                                        <Label className="text-[11px]">Tipo</Label>
                                        <select value={sc.commission_type || 'percentage'}
                                            onChange={e => setSuperCat(sc.slug, 'commission_type', e.target.value)}
                                            className="mt-1 w-full h-8 rounded-md border border-input bg-background px-2 text-xs">
                                            <option value="percentage">Porcentaje</option>
                                            <option value="fixed">Fijo</option>
                                            <option value="both">% + Fijo</option>
                                        </select>
                                    </div>
                                    {(sc.commission_type === 'percentage' || sc.commission_type === 'both' || !sc.commission_type) && (
                                        <div>
                                            <Label className="text-[11px]">Comisión (%)</Label>
                                            <div className="flex items-center gap-1 mt-1">
                                                <Input type="number" min="0" max="100" step="0.5"
                                                    value={sc.commission_pct}
                                                    onChange={e => setSuperCat(sc.slug, 'commission_pct', parseFloat(e.target.value) || 0)}
                                                    className="h-8 text-xs" />
                                                <span className="text-xs text-muted-foreground">%</span>
                                            </div>
                                        </div>
                                    )}
                                    {(sc.commission_type === 'fixed' || sc.commission_type === 'both') && (
                                        <div>
                                            <Label className="text-[11px]">Fijo (MXN)</Label>
                                            <div className="flex items-center gap-1 mt-1">
                                                <span className="text-xs text-muted-foreground">$</span>
                                                <Input type="number" min="0" step="0.5"
                                                    value={sc.fixed_fee}
                                                    onChange={e => setSuperCat(sc.slug, 'fixed_fee', parseFloat(e.target.value) || 0)}
                                                    className="h-8 text-xs" />
                                            </div>
                                        </div>
                                    )}
                                    <div>
                                        <Label className="text-[11px]">Pedido mínimo ($)</Label>
                                        <Input type="number" min="0" step="1"
                                            value={sc.min_order}
                                            onChange={e => setSuperCat(sc.slug, 'min_order', parseFloat(e.target.value) || 0)}
                                            className="mt-1 h-8 text-xs" />
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>

            <div className="flex justify-end">
                <Button onClick={onSave} disabled={saving} className="bg-red-600 hover:bg-red-700">
                    <Save className="h-4 w-4 mr-2" />{saving ? 'Guardando…' : 'Guardar configuración'}
                </Button>
            </div>
        </div>
    );
}

function HistoryTab() {
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(0);
    const [total, setTotal] = useState(0);
    const limit = 50;

    const load = async (p = 0) => {
        setLoading(true);
        try {
            const { data } = await api.get('/admin/okgo-commissions/transactions', {
                params: { skip: p * limit, limit }
            });
            setTransactions(data?.items || []);
            setTotal(data?.total || 0);
        } catch { toast.error('Error al cargar historial'); }
        finally { setLoading(false); }
    };

    useEffect(() => { load(page); }, [page]);

    if (loading && transactions.length === 0) return <div className="text-sm text-muted-foreground py-8 text-center">Cargando historial…</div>;

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground">{total} transacciones en total</p>
            </div>
            {transactions.length === 0 ? (
                <div className="rounded-xl border bg-card p-8 text-center">
                    <History className="h-8 w-8 mx-auto text-muted-foreground mb-3" />
                    <p className="text-sm text-muted-foreground">Sin transacciones de comisión aún.</p>
                    <p className="text-xs text-muted-foreground mt-1">Las comisiones se registran automáticamente al completarse un pedido en un OkNegocio.</p>
                </div>
            ) : (
                <div className="rounded-xl border bg-card overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b bg-neutral-50 text-xs text-muted-foreground">
                                    <th className="text-left px-4 py-2.5">Negocio</th>
                                    <th className="text-left px-4 py-2.5">Orden</th>
                                    <th className="text-left px-4 py-2.5">Total pedido</th>
                                    <th className="text-left px-4 py-2.5">Comisión</th>
                                    <th className="text-left px-4 py-2.5">%</th>
                                    <th className="text-left px-4 py-2.5">Estado</th>
                                    <th className="text-left px-4 py-2.5">Fecha</th>
                                </tr>
                            </thead>
                            <tbody>
                                {transactions.map(tx => (
                                    <tr key={tx.id} className="border-b last:border-0 hover:bg-neutral-50/80">
                                        <td className="px-4 py-2.5 font-medium">{tx.negocio_name}</td>
                                        <td className="px-4 py-2 text-xs font-mono text-muted-foreground">{tx.order_id?.slice(-8)}</td>
                                        <td className="px-4 py-2">{fmt(tx.order_total)}</td>
                                        <td className="px-4 py-2 font-medium text-red-700">{fmt(tx.commission_amount)}</td>
                                        <td className="px-4 py-2 text-muted-foreground">{tx.commission_pct ? `${tx.commission_pct}%` : '—'}</td>
                                        <td className="px-4 py-2">
                                            <Badge variant={tx.status === 'settled' ? 'default' : 'secondary'}
                                                className={tx.status === 'settled' ? 'bg-green-100 text-green-700 border-green-300' : ''}>
                                                {tx.status === 'settled' ? 'Liquidada' : tx.status === 'pending' ? 'Pendiente' : tx.status}
                                            </Badge>
                                        </td>
                                        <td className="px-4 py-2 text-xs text-muted-foreground">{tx.created_at?.slice(0, 10)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    {total > limit && (
                        <div className="border-t p-3 flex items-center justify-between text-xs text-muted-foreground">
                            <span>Página {page + 1} de {Math.ceil(total / limit)}</span>
                            <div className="flex gap-2">
                                <Button size="sm" variant="outline" onClick={() => setPage(p => p - 1)} disabled={page === 0}>Anterior</Button>
                                <Button size="sm" variant="outline" onClick={() => setPage(p => p + 1)} disabled={(page + 1) * limit >= total}>Siguiente</Button>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

function SettlementsTab() {
    const [settlements, setSettlements] = useState([]);
    const [loading, setLoading] = useState(false);
    const [expanded, setExpanded] = useState({});
    const [marking, setMarking] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get('/admin/okgo-commissions/settlements');
            setSettlements(data || []);
        } catch { toast.error('Error al cargar liquidaciones'); }
        finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []);

    const markSettled = async (negocio_id) => {
        setMarking(negocio_id);
        try {
            await api.post(`/admin/okgo-commissions/settle/${negocio_id}`);
            toast.success('Liquidación marcada como cobrada');
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || 'Error al liquidar'); }
        finally { setMarking(null); }
    };

    if (loading && settlements.length === 0) return <div className="text-sm text-muted-foreground py-8 text-center">Cargando liquidaciones…</div>;

    const totalPending = settlements.reduce((s, item) => s + (item.pending_amount || 0), 0);

    return (
        <div className="space-y-4">
            {settlements.length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="rounded-xl border bg-card p-4">
                        <div className="text-xs text-muted-foreground">Total pendiente por cobrar</div>
                        <div className="text-2xl font-semibold text-red-700 mt-1">{fmt(totalPending)}</div>
                    </div>
                    <div className="rounded-xl border bg-card p-4">
                        <div className="text-xs text-muted-foreground">Negocios con saldo pendiente</div>
                        <div className="text-2xl font-semibold mt-1">{settlements.filter(s => s.pending_amount > 0).length}</div>
                    </div>
                    <div className="rounded-xl border bg-card p-4">
                        <div className="text-xs text-muted-foreground">Total negocios</div>
                        <div className="text-2xl font-semibold mt-1">{settlements.length}</div>
                    </div>
                </div>
            )}

            {settlements.length === 0 ? (
                <div className="rounded-xl border bg-card p-8 text-center">
                    <Building2 className="h-8 w-8 mx-auto text-muted-foreground mb-3" />
                    <p className="text-sm text-muted-foreground">Sin liquidaciones pendientes.</p>
                </div>
            ) : (
                <div className="rounded-xl border bg-card divide-y overflow-hidden">
                    {settlements.map(s => (
                        <div key={s.negocio_id}>
                            <div className="flex items-center gap-3 p-4">
                                <div className="flex-1 min-w-0">
                                    <div className="font-medium text-sm">{s.negocio_name}</div>
                                    <div className="text-xs text-muted-foreground">{s.super_category} · {s.transaction_count} transacciones</div>
                                </div>
                                <div className="text-right shrink-0">
                                    <div className={`font-semibold text-sm ${s.pending_amount > 0 ? 'text-red-700' : 'text-green-700'}`}>
                                        {fmt(s.pending_amount)}
                                    </div>
                                    <div className="text-xs text-muted-foreground">pendiente</div>
                                </div>
                                <div className="flex items-center gap-1.5 shrink-0">
                                    {s.pending_amount > 0 && (
                                        <Button size="sm" variant="outline" className="h-8 text-xs"
                                            onClick={() => markSettled(s.negocio_id)}
                                            disabled={marking === s.negocio_id}>
                                            {marking === s.negocio_id ? 'Marcando…' : 'Cobrada'}
                                        </Button>
                                    )}
                                    <button onClick={() => setExpanded(p => ({ ...p, [s.negocio_id]: !p[s.negocio_id] }))}
                                        className="p-1.5 rounded hover:bg-secondary text-muted-foreground">
                                        {expanded[s.negocio_id] ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                                    </button>
                                </div>
                            </div>
                            {expanded[s.negocio_id] && (
                                <div className="px-4 pb-4 border-t pt-3 grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                                    <div><span className="text-muted-foreground">Total cobrado:</span><div className="font-medium">{fmt(s.total_collected)}</div></div>
                                    <div><span className="text-muted-foreground">Pendiente:</span><div className="font-medium text-red-700">{fmt(s.pending_amount)}</div></div>
                                    <div><span className="text-muted-foreground">Transacciones:</span><div className="font-medium">{s.transaction_count}</div></div>
                                    <div><span className="text-muted-foreground">Última actividad:</span><div className="font-medium">{s.last_transaction?.slice(0, 10) || '—'}</div></div>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

export default function AdminComisionesOkGo() {
    const [activeTab, setActiveTab] = useState('config');
    const [config, setConfig] = useState(null);
    const [superCats, setSuperCats] = useState([]);
    const [saving, setSaving] = useState(false);

    const load = async () => {
        try {
            const [{ data: cfg }, { data: cats }] = await Promise.all([
                api.get('/admin/okgo-commissions').catch(() => ({ data: null })),
                api.get('/admin/super-categories').catch(() => ({ data: [] })),
            ]);
            setSuperCats(cats || []);

            // Merge super-cat list into config
            const existingMap = Object.fromEntries(
                (cfg?.super_categories || []).map(sc => [sc.slug, sc])
            );
            const mergedCats = (cats || []).map(cat => ({
                slug: cat.slug,
                name: cat.name,
                icon: cat.icon,
                commission_type: existingMap[cat.slug]?.commission_type || 'percentage',
                commission_pct: existingMap[cat.slug]?.commission_pct ?? 15.0,
                fixed_fee: existingMap[cat.slug]?.fixed_fee ?? 0,
                min_order: existingMap[cat.slug]?.min_order ?? 0,
                active: existingMap[cat.slug]?.active ?? false,
            }));

            setConfig({
                default_commission_pct: cfg?.default_commission_pct ?? 15.0,
                default_fixed_fee: cfg?.default_fixed_fee ?? 0,
                default_min_order: cfg?.default_min_order ?? 0,
                default_commission_type: cfg?.default_commission_type || 'percentage',
                super_categories: mergedCats,
            });
        } catch { toast.error('Error al cargar configuración'); }
    };

    useEffect(() => { load(); }, []);

    const handleSave = async () => {
        setSaving(true);
        try {
            await api.put('/admin/okgo-commissions', config);
            toast.success('Configuración de comisiones OkGo guardada');
        } catch (e) { toast.error(e?.response?.data?.detail || 'Error al guardar'); }
        finally { setSaving(false); }
    };

    return (
        <div className="space-y-6 max-w-4xl">
            <div>
                <h1 className="text-2xl font-semibold tracking-tight">Comisiones OkGo</h1>
                <p className="text-sm text-muted-foreground">
                    Configura las comisiones que cobra la plataforma a cada OkNegocio por transacción, revisa el historial y gestiona liquidaciones.
                </p>
            </div>

            {/* Info banner */}
            <div className="rounded-xl border border-blue-200 bg-blue-50/60 p-4 flex gap-3 text-xs text-blue-800">
                <TrendingUp className="h-4 w-4 shrink-0 mt-0.5 text-blue-600" />
                <div>
                    <span className="font-medium">Funcionamiento: </span>
                    Al completarse un pedido en cualquier OkNegocio, se calcula automáticamente la comisión según la super categoría del negocio
                    y se registra en el historial. Las liquidaciones agrupan las comisiones pendientes de cobro por negocio.
                </div>
            </div>

            {/* Tabs */}
            <div className="border-b flex gap-0">
                {TABS.map(tab => (
                    <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                        className={`flex items-center gap-1.5 px-4 py-2.5 text-sm border-b-2 transition-colors ${activeTab === tab.id
                            ? 'border-red-600 text-red-700 font-medium'
                            : 'border-transparent text-muted-foreground hover:text-foreground'}`}>
                        <tab.icon className="h-3.5 w-3.5" />{tab.label}
                    </button>
                ))}
            </div>

            {activeTab === 'config' && (
                <ConfigTab config={config} setConfig={setConfig} superCats={superCats} saving={saving} onSave={handleSave} />
            )}
            {activeTab === 'history' && <HistoryTab />}
            {activeTab === 'settlements' && <SettlementsTab />}
        </div>
    );
}
